import React, { useState, useEffect } from 'react';
import {
  Box,
  Badge,
  Button,
  Flex,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  FormControl,
  FormLabel,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import * as XLSX from 'xlsx';
import Card from 'components/card/Card';
import JobReferralsApi from '../../../../api/jobReferrals';
import { useCompaniesOptions } from '../../../../hooks/useCompaniesOptions';
import User from '../../../../api/user';

// Status sets — position-based vs general referral
const POSITION_STATUS_OPTIONS = [
  { value: 'link_created', label: 'קישור נוצר' },
  { value: 'applied', label: 'הוגשה מועמדות' },
  { value: 'hired', label: 'התקבל' },
  { value: 'not_hired', label: 'לא התקבל' },
  { value: 'no_match', label: 'לא נמצאה התאמה' },
];

const GENERAL_STATUS_OPTIONS = [
  { value: 'new', label: 'בתהליך' },
  { value: 'hired', label: 'התקבל' },
  { value: 'not_hired', label: 'לא התקבל' },
  { value: 'no_match', label: 'לא נמצאה התאמה' },
];

// Combined filter options (all statuses)
const ALL_STATUS_OPTIONS = [
  { value: '', label: 'כל הסטטוסים' },
  { value: 'link_created', label: 'קישור נוצר' },
  { value: 'applied', label: 'הוגשה מועמדות' },
  { value: 'new', label: 'בתהליך (כללי)' },
  { value: 'hired', label: 'התקבל' },
  { value: 'not_hired', label: 'לא התקבל' },
  { value: 'no_match', label: 'לא נמצאה התאמה' },
];

const STATUS_LABELS = {
  link_created: 'קישור נוצר',
  applied: 'הוגשה מועמדות',
  hired: 'התקבל',
  not_hired: 'לא התקבל',
  no_match: 'לא נמצאה התאמה',
  new: 'בתהליך',
  // Legacy
  contact_made: 'נוצר קשר',
  not_relevant: 'נדחה',
};

const STATUS_COLORS = {
  link_created: 'purple',
  applied: 'blue',
  hired: 'green',
  not_hired: 'red',
  no_match: 'gray',
  new: 'blue',
  contact_made: 'blue',
  not_relevant: 'red',
};

const PAGE_SIZE = 50;

export default function ReferralsTab({ isSuperAdmin }) {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ companyId: '', status: '', dateFrom: '', dateTo: '' });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [hireDate, setHireDate] = useState('');
  const [bonusAmount, setBonusAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [cvLoading, setCvLoading] = useState(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const { companiesOptions } = useCompaniesOptions({ showAll: true });

  const fetchReferrals = async (currentPage = page) => {
    setLoading(true);
    try {
      const params = {
        companyId: isSuperAdmin && filters.companyId && filters.companyId !== 'ALL' ? filters.companyId : undefined,
        status: filters.status || undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
        page: currentPage,
        limit: PAGE_SIZE,
      };
      const res = await JobReferralsApi.getReferrals(params);
      setReferrals(res.data?.data || []);
      setTotal(res.data?.total || 0);
    } catch {
      toast({ title: 'שגיאה בטעינת הפניות', status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferrals(1);
    setPage(1);
  }, []);

  const openStatusModal = (referral) => {
    setSelectedReferral(referral);
    setNewStatus(referral.status);
    setHireDate('');
    setBonusAmount('');
    onOpen();
  };

  const handleViewCv = async (referralId) => {
    setCvLoading(referralId);
    try {
      const res = await JobReferralsApi.getCvSignedUrl(referralId);
      window.open(res.data.url, '_blank');
    } catch {
      toast({ title: 'שגיאה בטעינת קורות חיים', status: 'error', duration: 3000 });
    } finally {
      setCvLoading(null);
    }
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) return;
    if (newStatus === 'hired' && !hireDate) {
      toast({ title: 'יש לבחור תאריך קבלה', status: 'warning', duration: 3000 });
      return;
    }
    if (newStatus === 'hired' && selectedReferral?.type === 'general' && !bonusAmount) {
      toast({ title: 'יש להזין סכום בונוס להפניה כללית', status: 'warning', duration: 3000 });
      return;
    }
    setSaving(true);
    try {
      await JobReferralsApi.updateReferralStatus(
        selectedReferral.id,
        newStatus,
        hireDate || undefined,
        selectedReferral?.type === 'general' && bonusAmount ? Number(bonusAmount) : undefined
      );
      toast({ title: 'סטטוס עודכן', status: 'success', duration: 2000 });
      onClose();
      fetchReferrals(page);
    } catch {
      toast({ title: 'שגיאה בעדכון סטטוס', status: 'error', duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = () => {
    const rows = referrals.map((r) => ({
      'תאריך יצירה': r.linkCreatedAt ? new Date(r.linkCreatedAt).toLocaleDateString('he-IL') : '',
      'חברה': companiesOptions.find((c) => String(c.value) === String(r.companyId))?.label || r.companyId || '',
      'סוג הפניה': r.type === 'general' ? 'כללי' : (r.job?.title || 'משרה'),
      'שם מועמד': r.candidate?.fullName || '',
      'טלפון מועמד': r.candidate?.phone || '',
      'ת.ז. מועמד': r.candidate?.govId || '',
      'אימייל מועמד': r.candidate?.email || '',
      'עיר מועמד': r.candidate?.city || '',
      'הערות': r.candidate?.notes || '',
      'ממליץ': r.referrer ? `${r.referrer.firstName} ${r.referrer.lastName}` : '',
      'טלפון ממליץ': r.referrer?.phone || '',
      'סטטוס': STATUS_LABELS[r.status] || r.status,
      'תאריך הגשה': r.appliedAt ? new Date(r.appliedAt).toLocaleDateString('he-IL') : '',
      'תאריך קבלה': r.hiredAt ? new Date(r.hiredAt).toLocaleDateString('he-IL') : '',
      'כפיל': r.isDuplicate ? 'כן' : '',
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'הפניות');
    XLSX.writeFile(wb, 'job-referrals.xlsx');
  };

  const isGeneralReferral = (r) => r.type === 'general';
  const statusOptionsForReferral = (r) =>
    isGeneralReferral(r) ? GENERAL_STATUS_OPTIONS : POSITION_STATUS_OPTIONS;

  return (
    <Card mt={4}>
      {/* Filters */}
      <Flex wrap="wrap" gap={3} mb={4} align="flex-end">
        {isSuperAdmin && (
          <FormControl w="200px">
            <FormLabel fontSize="sm">חברה</FormLabel>
            <Select
              value={filters.companyId}
              onChange={(e) => setFilters({ ...filters, companyId: e.target.value })}
              dir="rtl"
            >
              {companiesOptions.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControl w="200px">
          <FormLabel fontSize="sm">סטטוס</FormLabel>
          <Select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            dir="rtl"
          >
            {ALL_STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl w="160px">
          <FormLabel fontSize="sm">מתאריך</FormLabel>
          <Input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
          />
        </FormControl>
        <FormControl w="160px">
          <FormLabel fontSize="sm">עד תאריך</FormLabel>
          <Input
            type="date"
            value={filters.dateTo}
            onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
          />
        </FormControl>
        <HStack>
          <Button colorScheme="blue" size="sm" onClick={() => { setPage(1); fetchReferrals(1); }}>
            חיפוש
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} isDisabled={referrals.length === 0}>
            ייצוא Excel
          </Button>
        </HStack>
      </Flex>

      {loading ? (
        <Flex justify="center" py={8}>
          <Spinner />
        </Flex>
      ) : (
        <Box overflowX="auto">
          <Table variant="simple" dir="rtl">
            <Thead>
              <Tr>
                <Th color={textColor} borderColor={borderColor}>תאריך</Th>
                <Th color={textColor} borderColor={borderColor}>חברה</Th>
                <Th color={textColor} borderColor={borderColor}>מועמד</Th>
                <Th color={textColor} borderColor={borderColor}>טלפון</Th>
                <Th color={textColor} borderColor={borderColor}>ת.ז.</Th>
                <Th color={textColor} borderColor={borderColor}>אימייל</Th>
                <Th color={textColor} borderColor={borderColor}>עיר</Th>
                <Th color={textColor} borderColor={borderColor}>סוג / משרה</Th>
                <Th color={textColor} borderColor={borderColor}>ממליץ</Th>
                <Th color={textColor} borderColor={borderColor}>סטטוס</Th>
                <Th color={textColor} borderColor={borderColor}>קו"ח</Th>
                <Th color={textColor} borderColor={borderColor}>שינוי סטטוס</Th>
              </Tr>
            </Thead>
            <Tbody>
              {referrals.length === 0 && (
                <Tr>
                  <Td colSpan={12} textAlign="center" color="gray.400">
                    אין הפניות
                  </Td>
                </Tr>
              )}
              {referrals.map((r) => (
                <Tr key={r.id}>
                  <Td color={textColor} fontSize="sm">
                    {r.linkCreatedAt ? new Date(r.linkCreatedAt).toLocaleDateString('he-IL') : '—'}
                  </Td>
                  <Td color={textColor} fontSize="sm">
                    {companiesOptions.find((c) => String(c.value) === String(r.companyId))?.label || User.GetInfo()?.companyName || r.companyId || '—'}
                  </Td>
                  <Td color={textColor}>
                    <Flex align="center" gap={1} wrap="wrap">
                      {r.candidate?.fullName || '—'}
                      {r.isDuplicate && (
                        <Badge colorScheme="orange" fontSize="10px" ml={1}>כפיל</Badge>
                      )}
                    </Flex>
                  </Td>
                  <Td color={textColor}>{r.candidate?.phone || '—'}</Td>
                  <Td color={textColor}>{r.candidate?.govId || '—'}</Td>
                  <Td color={textColor}>{r.candidate?.email || '—'}</Td>
                  <Td color={textColor}>{r.candidate?.city || '—'}</Td>
                  <Td color={textColor}>
                    {isGeneralReferral(r) ? (
                      <Badge colorScheme="purple" fontSize="11px">כללי</Badge>
                    ) : (
                      r.job?.title || '—'
                    )}
                  </Td>
                  <Td color={textColor}>
                    {r.referrer ? `${r.referrer.firstName} ${r.referrer.lastName}` : '—'}
                  </Td>
                  <Td>
                    <Badge colorScheme={STATUS_COLORS[r.status] || 'gray'}>
                      {STATUS_LABELS[r.status] || r.status}
                    </Badge>
                  </Td>
                  <Td>
                    {r.candidate?.cvFilePath ? (
                      <Button
                        size="xs"
                        colorScheme="teal"
                        variant="outline"
                        isLoading={cvLoading === r.id}
                        onClick={() => handleViewCv(r.id)}
                      >
                        צפה בקו"ח
                      </Button>
                    ) : (
                      <Text fontSize="xs" color="gray.400">—</Text>
                    )}
                  </Td>
                  <Td>
                    <Button size="xs" variant="outline" onClick={() => openStatusModal(r)}>
                      עדכן
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Pagination */}
      {total > 0 && (
        <Flex justify="center" align="center" gap={4} mt={4}>
          <Button size="sm" onClick={() => { const p = page - 1; setPage(p); fetchReferrals(p); }} isDisabled={page === 1}>
            הקודם
          </Button>
          <Flex direction="column" align="center">
            <Text fontSize="sm">{page} / {Math.ceil(total / PAGE_SIZE)}</Text>
            <Text fontSize="xs" color="gray.400">{total} סה"כ</Text>
          </Flex>
          <Button size="sm" onClick={() => { const p = page + 1; setPage(p); fetchReferrals(p); }} isDisabled={page >= Math.ceil(total / PAGE_SIZE)}>
            הבא
          </Button>
        </Flex>
      )}

      {/* Status Update Modal */}
      <Modal isOpen={isOpen} onClose={onClose} dir="rtl">
        <ModalOverlay />
        <ModalContent dir="rtl">
          <ModalHeader>עדכון סטטוס הפניה</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" gap={4}>
              <Text fontSize="sm" color="gray.500">
                מועמד: {selectedReferral?.candidate?.fullName || '—'}
                {selectedReferral?.type === 'general'
                  ? ' | הפניה כללית'
                  : ` | משרה: ${selectedReferral?.job?.title || '—'}`}
              </Text>
              <FormControl>
                <FormLabel>סטטוס חדש</FormLabel>
                <Select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  dir="rtl"
                >
                  {statusOptionsForReferral(selectedReferral || {}).map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
              {newStatus === 'hired' && (
                <FormControl isRequired>
                  <FormLabel>תאריך קבלה</FormLabel>
                  <Input
                    type="date"
                    value={hireDate}
                    onChange={(e) => setHireDate(e.target.value)}
                  />
                </FormControl>
              )}
              {newStatus === 'hired' && selectedReferral?.type === 'general' && (
                <FormControl isRequired>
                  <FormLabel>סכום בונוס (₪)</FormLabel>
                  <Input
                    type="number"
                    min="0"
                    placeholder="לדוגמה: 300"
                    value={bonusAmount}
                    onChange={(e) => setBonusAmount(e.target.value)}
                  />
                </FormControl>
              )}
            </Flex>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button colorScheme="blue" isLoading={saving} onClick={handleStatusUpdate}>
              שמור
            </Button>
            <Button variant="ghost" onClick={onClose}>
              ביטול
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
}
